import { css } from '@emotion/css';
import { useGetEventPage } from '@src/apis/queries';
import { DagooCard } from '@src/components/DagooCard';
import { ErrorModal } from '@src/components/ErrorModal';
import { EventPopular } from '@src/components/EventPopular';
import { LoadingSpinner } from '@src/components/LoadingSpinner';
import { ProjectItem } from '@src/components/ProjectItem';
import { TopNavigation } from '@src/components/TopNavigationBar';
import { usePostMessage } from '@src/hooks/usePostMessage';
import { Event } from '@src/models/event';
import { trackEvent } from '@src/utils/analytics';
import { useCallback, useEffect, useState } from 'react';

export const EventPage = () => {
  const { data: eventPageData, isLoading, isError } = useGetEventPage();
  const [eventData, setEventData] = useState<Event>();
  const [scrollY, setScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const postMessageToParent = usePostMessage();
  /**
   * 부모 창(Flutter 앱 또는 브라우저)에 페이지 닫기 메시지를 전송하거나 뒤로가기를 실행하는 함수
   * Flutter 환경에서는 postMessage를 통해 닫기 신호를 전달하고, 브라우저 환경에서는 history.back()을 호출
   */
  const sendMessageToParent = () => {
    const closeMessage: PostMessageType<ActionMessage> = {
      type: 'action',
      data: {
        messageDataType: 'close',
        payload: { status: true },
      },
    };

    if (window.isFlutter()) postMessageToParent<ActionMessage>(closeMessage);
    else history.back();
  };

  /**
   * API로부터 받은 이벤트 페이지 데이터를 state에 저장하는 effect
   * eventPageData가 변경될 때마다 eventData state를 업데이트
   */
  useEffect(() => {
    if (eventPageData?.data) {
      setEventData(eventPageData.data as Event);
    }
  }, [eventPageData]);

  /**
   * 이벤트 페이지 조회 시 분석 이벤트를 추적하는 effect
   * eventData가 로드되면 사용자의 플레이 유형과 함께 페이지 조회 이벤트를 전송
   */
  useEffect(() => {
    if (eventData) {
      trackEvent({
        eventName: 'view_event_page',
        properties: {
          playType: eventData.preference.name,
        },
      });
    }
  }, [eventData]);

  /**
   * eventData의 유효성을 검증하여 에러 모달 표시 여부를 결정하는 함수
   * - API 호출 실패 시
   * - preference 데이터가 불완전한 경우
   * - popularProjects가 비어있는 경우
   * - localProjects.projects가 비어있는 경우
   * - recommendProjects.projects가 비어있는 경우
   * 위 조건 중 하나라도 해당되면 true를 반환
   */
  const shouldShowErrorModal = useCallback(() => {
    // API 호출 실패 또는 응답 데이터가 없는 경우
    if (isError || !eventPageData?.data) {
      return true;
    }

    const data = eventPageData.data as Event;

    // preference 데이터 검증
    if (!data.preference || !data.preference.imageUrl || !data.preference.name || !data.preference.description) {
      return true;
    }

    // popularProjects가 비어있는 경우
    if (!data.popularProjects || data.popularProjects.length === 0) {
      return true;
    }

    // localProjects.projects가 비어있는 경우
    if (!data.localProjects || !data.localProjects.projects || data.localProjects.projects.length === 0) {
      return true;
    }

    // recommendProjects.projects가 비어있는 경우
    if (!data.recommendProjects || !data.recommendProjects.projects || data.recommendProjects.projects.length === 0) {
      return true;
    }

    return false;
  }, [isError, eventPageData]);

  /**
   * API 응답 및 eventData 상태 변경 시 에러 모달 표시 여부를 판단하는 effect
   * 로딩이 완료된 후 데이터 유효성을 검증하여 모달을 표시
   */
  useEffect(() => {
    if (!isLoading) {
      setIsModalOpen(shouldShowErrorModal());
    }
  }, [isLoading, shouldShowErrorModal]);

  /**
   * 스크롤 이벤트를 감지하여 현재 스크롤 위치를 state에 저장하는 effect
   * 스크롤 위치에 따라 네비게이션 바의 배경색과 텍스트 색상을 동적으로 변경하기 위해 사용
   */
  useEffect(() => {
    const scrollContainer = document.querySelector('.mobile-wrapper');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      setScrollY(scrollTop);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * 로컬 프로젝트 목록을 ProjectItem 컴포넌트로 렌더링하는 함수
   * eventData에 localProjects가 있을 경우 각 프로젝트를 ProjectItem으로 변환하여 반환
   */
  const renderLocalProjects = () => {
    if (eventData?.localProjects) {
      return eventData.localProjects.projects.map((project, idx) => (
        <ProjectItem
          key={idx}
          project={project}
          preferenceName={eventData.preference.name}
          section='local'
          isEvent
          isShowPrice
        />
      ));
    }
  };

  /**
   * 추천 프로젝트 목록을 ProjectItem 컴포넌트로 렌더링하는 함수
   * eventData에 recommendProjects가 있을 경우 각 프로젝트를 ProjectItem으로 변환하여 반환
   */
  const renderRecommendProjects = () => {
    if (eventData?.recommendProjects) {
      return eventData.recommendProjects.projects.map((project, idx) => (
        <ProjectItem
          key={idx}
          project={project}
          preferenceName={eventData.preference.name}
          section='recommendation'
          isEvent
          isShowPrice
        />
      ));
    }
  };

  /**
   * 현재 스크롤 위치에 따라 네비게이션 바의 배경색을 계산하는 함수
   * 스크롤이 0일 때는 투명, 120px 이상일 때는 완전히 흰색으로 변경
   * 반환값은 hex 색상 코드 형식 (#ffffff + 투명도)
   */
  const getBackgroundColor = () => {
    const opacity = Math.min(scrollY / 120, 1);
    const hexOpacity = Math.round(opacity * 255)
      .toString(16)
      .padStart(2, '0');
    return `#ffffff${hexOpacity}`;
  };

  /**
   * 현재 스크롤 위치에 따라 네비게이션 바의 텍스트 및 아이콘 색상을 결정하는 함수
   * 스크롤이 60px 이상이면 검은색, 미만이면 흰색을 반환
   * 배경이 어두울 때는 흰색, 밝아질 때는 검은색으로 가독성을 확보
   */
  const getTextColor = () => {
    return scrollY >= 60 ? 'black' : 'white';
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <ErrorModal isOpen={isModalOpen} onClose={sendMessageToParent} />
      <TopNavigation
        isClose={false}
        arrowColor={getTextColor()}
        titleColor={getTextColor()}
        hexColor={getBackgroundColor()}
        onClickBack={sendMessageToParent}
        onClickClose={sendMessageToParent}
        title='추천 콘텐츠'
      />
      <div className={EventPageStyle}>
        <div className={GapBetweenTopAndCardStyle} />
        <div className={DagooCardSectionStyle}>
          {eventData?.preference && <DagooCard preference={eventData.preference} />}
        </div>
        <div className={EventRecommendationSectionStyle}>
          {eventData?.popularProjects && (
            <EventPopular
              userName={eventData.userName}
              preferenceSimilarity={eventData.preferenceSimilarity}
              popularProjects={eventData.popularProjects}
              preferenceName={eventData.preference.name}
            />
          )}
        </div>
        <div className={ProjectListSectionStyle}>
          {eventData?.localProjects && <p className={ProjectListSectionTitleStyle}>{eventData?.localProjects.title}</p>}
          {eventData?.localProjects && renderLocalProjects()}
        </div>
        <div className={ProjectListSectionStyle}>
          {eventData?.recommendProjects && (
            <p className={ProjectListSectionTitleStyle}>{eventData?.recommendProjects.title}</p>
          )}
          {eventData?.recommendProjects && renderRecommendProjects()}
        </div>
      </div>
    </>
  );
};

const EventPageStyle = css`
  width: 100%;
  height: 100%;
  gap: 12px;
`;

const GapBetweenTopAndCardStyle = css`
  height: 48px;
  background-color: #3e0085;
`;

const DagooCardSectionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #3e0085;
  padding-top: 16px;
`;

const EventRecommendationSectionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 50px;
  background: #3e0085;
`;

const ProjectListSectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
  padding-top: 48px;
`;

const ProjectListSectionTitleStyle = css`
  color: var(--text-text-default, #1a1a1a);
  white-space: pre-wrap;

  font-size: var(--font-size-font-size-3, 18px);
  font-style: normal;
  font-weight: var(--font-weight-font-weight-extrabold, 800);
  line-height: var(--line-height-font-height-3, 26px); /* 144.444% */
`;
